import {
    MicroinvertersNotReportingTroubleshooting,
    GatewayNonCriticalIssueTroubleshooting,
    SystemControllerIssueTroubleshooting,
    SystemDisconnectedTroubleshooting,
    MediumImpactAlertTroubleshooting,
    BatteryIssueTroubleshooting,
    SystemIssueTroubleshooting,
    IconAlarmClockOff,
    IconAlertTriangle,
    IconBatteryEmpty,
    IconBatteryLow,
    IconGitBranch02,
    IconGitConnect,
    IconMessageAlertCircle,
    IconMessageAlertSquare,
    IconMessageQuestionCircle,
    IconMessageQuestionSquare,
    IconMessageXCircle,
    IconMessageXSquare,
    IconMicroInverter,
    IconModem,
    IconReloadCw05,
    IconSize,
    IconSliders,
    IconSpeedometer03,
    IconSpeedometer04,
    IconSunSetting01,
    IconWifiOff,
    IconZapOff
} from '@/components'
import { InverterManufacturerEnum, SystemStatusEnum } from '@/enums'
import { feature } from '@/services'
import { SystemStatusDataType } from '@/types'

export const SYSTEM_STATUSES_MONITORED_TIER_2: SystemStatusDataType[] = [
    {
        value: SystemStatusEnum.IMPACT_ALERT_LEVEL_6,
        title: 'Medium Impact Alert - Level 6',
        refreshable: true,
        secondaryOrder: 6,
        icon: () => 6,
        troubleshooting: <MediumImpactAlertTroubleshooting/>,
        description: 'Alert with medium severity score, suggesting a moderate risk that may escalate without action.' +
            ' For detailed information, please visit your SolarEdge Monitoring portal. Solar Insure only retrieves' +
            ' the Impact Indicator score (1-9), calculated by SolarEdge based on potential energy loss.'
    },
    {
        value: SystemStatusEnum.IMPACT_ALERT_LEVEL_5,
        title: 'Medium Impact Alert - Level 5',
        refreshable: true,
        secondaryOrder: 7,
        icon: () => 5,
        troubleshooting: <MediumImpactAlertTroubleshooting/>,
        description: 'Noticeable alert requiring attention, with medium impact on system performance. For detailed' +
            ' information, please visit your SolarEdge Monitoring portal. Solar Insure only retrieves the Impact' +
            ' Indicator score (1-9), calculated by SolarEdge based on potential energy loss.'
    },
    {
        value: SystemStatusEnum.IMPACT_ALERT_LEVEL_4,
        title: 'Medium Impact Alert - Level 4',
        refreshable: true,
        secondaryOrder: 8,
        icon: () => 4,
        troubleshooting: <MediumImpactAlertTroubleshooting/>,
        description: 'Alert indicating a moderate issue, needs monitoring to avoid further system impact. For' +
            ' detailed information, please visit your SolarEdge Monitoring portal. Solar Insure only retrieves the' +
            ' Impact Indicator score (1-9), calculated by SolarEdge based on potential energy loss.'
    },
    {
        value: SystemStatusEnum.MICRO,
        title: 'Microinverters Not Reporting',
        refreshable: true,
        secondaryOrder: 1,
        icon: (size: IconSize) => <IconMicroInverter size={size}/>,
        troubleshooting: <MicroinvertersNotReportingTroubleshooting/>,
        description: 'One or more microinverters are not communicating, potentially due to range or interference' +
            ' issues. This can usually be resolved remotely.'
    },
    {
        value: SystemStatusEnum.ENCHARGE_ISSUE,
        title: 'Battery Issue',
        refreshable: true,
        secondaryOrder: 2,
        icon: (size: IconSize) => <IconBatteryLow size={size}/>,
        troubleshooting: <BatteryIssueTroubleshooting/>,
        description: 'Detected problem with battery, needs checking to ensure proper storage performance.'
    },
    {
        value: SystemStatusEnum.SYSTEM,
        title: 'System Issue',
        secondaryOrder: 3,
        icon: (size: IconSize) => <IconAlertTriangle size={size}/>,
        troubleshooting: <SystemIssueTroubleshooting/>,
        description: 'General alert for an undefined system problem that requires further investigation.'
    },
    {
        value: SystemStatusEnum.ENPOWER_ISSUE,
        title: 'System Controller Issue',
        refreshable: true,
        secondaryOrder: 4,
        icon: (size: IconSize) => <IconSliders size={size}/>,
        description: 'The system control unit is reporting a malfunction, needs attention to maintain system' +
            ' integrity.',
        troubleshooting: <SystemControllerIssueTroubleshooting/>
    },
    {
        value: SystemStatusEnum.GATEWAY,
        title: 'Gateway Non-Critical Issue',
        refreshable: true,
        secondaryOrder: 5,
        icon: (size: IconSize) => <IconModem size={size}/>,
        troubleshooting: <GatewayNonCriticalIssueTroubleshooting/>,
        description: 'Assigned to gateway issues detected within the last 7 days. Gateway issues typically become' +
            ' critical if they persist for more than a week, suggesting ongoing component problems or alterations in' +
            ' the connection setup that require manual resolution.'
    },
    {
        value: SystemStatusEnum.SYSTEM_DISCONNECTED,
        title: 'System Disconnected',
        refreshable: true,
        secondaryOrder: 9,
        icon: (size: IconSize) => <IconWifiOff size={size}/>,
        troubleshooting: <SystemDisconnectedTroubleshooting/>,
        description: 'One or more systems in this site have temporarily lost internet connectivity.'
    },
    {
        value: SystemStatusEnum.BATTERY_BREAKER_OPEN,
        title: 'Battery Breaker Open',
        refreshable: true,
        secondaryOrder: 10,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconBatteryEmpty size={size}/>,
        description: 'There is an open breaker between the site meter and the Powerwall itself. The meter will' +
            ' automatically detect when the breaker is closed again.'
    },
    {
        value: SystemStatusEnum.BATTERY_METER_COMMS,
        title: 'Battery Meter Comms',
        refreshable: true,
        secondaryOrder: 11,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconMessageAlertCircle size={size}/>,
        description: 'Loss of communications with Battery Meter.'
    },
    {
        value: SystemStatusEnum.BUSWAY_METER_COMMS,
        title: 'Busway Meter Comms',
        refreshable: true,
        secondaryOrder: 12,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconMessageAlertSquare size={size}/>,
        description: 'Loss of communications with Busway Meter.'
    },
    {
        value: SystemStatusEnum.EXTERNAL_IO_COMMS,
        title: 'External IO Comms',
        refreshable: true,
        secondaryOrder: 13,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconMessageXCircle size={size}/>,
        description: 'Loss of communications with External IO device.'
    },
    {
        value: SystemStatusEnum.FREQUENCY_METER_COMMS,
        title: 'Frequency Meter Comms',
        refreshable: true,
        secondaryOrder: 14,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconMessageXSquare size={size}/>,
        description: 'Loss of communications with Frequency Meter.'
    },
    {
        value: SystemStatusEnum.GENERATOR_BREAKER_TIMEOUT,
        title: 'Generator Breaker Timeout',
        refreshable: true,
        secondaryOrder: 15,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconAlarmClockOff size={size}/>,
        description:
            'The generator was unable to close its breaker within the timeout window.'
    },
    {
        value: SystemStatusEnum.GENERATOR_METER_COMMS,
        title: 'Generator Meter Comms',
        refreshable: true,
        secondaryOrder: 16,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconSpeedometer04 size={size}/>,
        description: 'Loss of communications with Generator Meter.'
    },
    {
        value: SystemStatusEnum.GRID_RESYNCHRONIZATION_FAILED,
        title: 'Grid Resynchronization Failed',
        refreshable: true,
        secondaryOrder: 17,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconReloadCw05 size={size}/>,
        description: 'The islanding controller could not synchronize and close the islanding contactor/circuit' +
            ' breaker within 5 minutes.'
    },
    {
        value: SystemStatusEnum.LOST_CONNECTIVITY_TO_SITE_CONTROLLER,
        title: 'Lost Connectivity to Tesla Site Controller',
        refreshable: true,
        secondaryOrder: 18,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconGitBranch02 size={size}/>,
        description: 'The Tesla Site Controller (or Gateway) has lost connectivity to Tesla Servers. Monitoring' +
            ' and management capabilities are currently limited.'
    },
    {
        value: SystemStatusEnum.LOAD_METER_COMMS,
        title: 'Load Meter Comms',
        refreshable: true,
        secondaryOrder: 19,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconSpeedometer03 size={size}/>,
        description: 'Loss of communications with Load Meter.'
    },
    {
        value: SystemStatusEnum.PV_INVERTER_COMMS,
        title: 'PV Inverter Comms',
        refreshable: true,
        secondaryOrder: 20,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconGitConnect size={size}/>,
        description: 'Loss of communications with PV Inverter.'
    },
    {
        value: SystemStatusEnum.PYRANOMETER_COMMS,
        title: 'Pyranometer Comms',
        refreshable: true,
        secondaryOrder: 21,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconMessageQuestionCircle size={size}/>,
        description: 'Loss of communications with Pyranometer.'
    },
    {
        value: SystemStatusEnum.SITE_METER_COMMS,
        title: 'Site Meter Comms',
        refreshable: true,
        secondaryOrder: 22,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconMessageQuestionSquare size={size}/>,
        description: 'Loss of communications with Site Meter.'
    },
    {
        value: SystemStatusEnum.SOLAR_METER_COMMS,
        title: 'Solar Meter Comms',
        refreshable: true,
        secondaryOrder: 23,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconSunSetting01 size={size}/>,
        description: 'Loss of communications with Solar Meter.'
    },
    {
        value: SystemStatusEnum.SYSTEM_SHUTDOWN,
        title: 'System Shutdown',
        refreshable: true,
        secondaryOrder: 24,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconZapOff size={size}/>,
        description: 'An external kill switch has shut the system down, or a low GPIO pin has triggered a system' +
            ' shutdown.'
    },
    {
        value: SystemStatusEnum.WAIT_FOR_SOLAR,
        title: 'Wait for Solar',
        refreshable: true,
        secondaryOrder: 25,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: () => 6,
        description: 'The site doesn\'t have enough SOE to form a grid. Fix: user command or wait for daytime.'
    }
].filter(({ manufacturer }) => manufacturer !== InverterManufacturerEnum.TESLA || feature('tesla-integration'))
